import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { useLanguage } from '../../context/LanguageContext';
const IMAGES = ['https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80', 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80', 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80', 'https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=800&q=80', 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?w=800&q=80'];
export function PatientJourneyScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    t
  } = useLanguage();
  const steps = t.patients.visualJourney.steps;
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const cardWidth = scrollRef.current.offsetWidth; // Approximate width of one snap area
    // Calculate active index based on scroll position
    // Adding half width to center the detection point
    const index = Math.round(scrollLeft / (cardWidth * 0.6));
    // Clamp index
    const clampedIndex = Math.max(0, Math.min(index, steps.length - 1));
    setActiveIndex(clampedIndex);
  };
  // More precise intersection observer approach for active state
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          setActiveIndex(index);
        }
      });
    }, {
      root: container,
      threshold: 0.6 // Trigger when 60% of the card is visible
    });
    const cards = container.querySelectorAll('.journey-card');
    cards.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, [steps]);
  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.querySelector('.journey-card')?.clientWidth || 300;
    const gap = 32; // gap-8 is 2rem = 32px
    const scrollAmount = cardWidth + gap;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };
  const scrollToStep = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const card = container.querySelectorAll('.journey-card')[index] as HTMLElement;
    if (card) {
      // Center the card
      const containerWidth = container.offsetWidth;
      const cardWidth = card.offsetWidth;
      const cardLeft = card.offsetLeft;
      // Calculate scroll position to center the card
      // We need to account for the container's padding/offset if any, but scrollLeft is relative to content
      // Simple scrollIntoView works well for centering with smooth behavior
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };
  return <section className="py-20 bg-white overflow-hidden" data-id="element-178">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-179">
        <SectionHeading title={t.patients.visualJourney.title} subtitle={t.patients.visualJourney.subtitle} data-id="element-180" />

        <div className="relative group" data-id="element-181">
          {/* Scroll Container */}
          <div ref={scrollRef} className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-182">
            {steps.map((step, index) => <div key={index} data-index={index} className="journey-card flex-shrink-0 w-[85vw] md:w-[400px] lg:w-[450px] snap-center md:snap-start relative rounded-2xl overflow-hidden shadow-lg transition-transform duration-300 hover:scale-[1.01]" data-id="element-183">
                {/* Image Container */}
                <div className="aspect-[4/5] md:aspect-[3/4] relative" data-id="element-184">
                  <img src={IMAGES[index]} alt={step.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-id="element-185" />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-caces-navy/90 via-caces-navy/40 to-transparent" data-id="element-186" />

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white" data-id="element-187">
                    <div className="flex items-center gap-3 mb-3" data-id="element-188">
                      <span className="text-4xl font-serif font-bold text-white/20" data-id="element-189">
                        0{index + 1}
                      </span>
                      <div className="h-px bg-white/30 flex-grow" data-id="element-190" />
                    </div>
                    <h3 className="text-2xl font-serif font-bold mb-2 leading-tight" data-id="element-191">
                      {step.title}
                    </h3>
                    <p className="text-caces-light/90 text-sm leading-relaxed font-medium" data-id="element-192">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>)}

            {/* Spacer for right padding on mobile scroll */}
            <div className="w-4 flex-shrink-0 md:hidden" data-id="element-193" />
          </div>

          {/* Desktop Navigation Buttons */}
          <button onClick={() => scroll('left')} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -ml-4 lg:-ml-12 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center text-caces-navy hover:text-caces-accent hover:bg-gray-50 transition-all z-10 opacity-0 group-hover:opacity-100 disabled:opacity-0" disabled={activeIndex === 0} aria-label="Scroll left" data-id="element-194">
            <ChevronLeft size={24} data-id="element-195" />
          </button>
          <button onClick={() => scroll('right')} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 -mr-4 lg:-mr-12 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center text-caces-navy hover:text-caces-accent hover:bg-gray-50 transition-all z-10 opacity-0 group-hover:opacity-100 disabled:opacity-0" disabled={activeIndex === steps.length - 1} aria-label="Scroll right" data-id="element-196">
            <ChevronRight size={24} data-id="element-197" />
          </button>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center gap-3 mt-8" data-id="element-198">
          {steps.map((_, index) => <button key={index} onClick={() => scrollToStep(index)} className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'w-8 bg-caces-accent' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to step ${index + 1}`} aria-current={index === activeIndex} data-id="element-199" />)}
        </div>
      </div>
    </section>;
}